import { z } from 'zod'
import { email } from '~/services/api/contracts/email'
import { fileUpload } from '~/services/api/contracts/fileUpload'
import { phone } from '~/services/api/contracts/phone'
import { StoreUpdateDetailsRequestSchema } from '~/services/api/generated'
import type { StoreStatus } from '~/services/api/generated'
import { GOOGLE_PLACES_ADDRESS_MAX } from '~/services/api/contracts/bounds/GOOGLE_PLACES_ADDRESS_MAX'
import { isStoreOnboardingEditable } from '~/utils/isStoreOnboardingEditable'
import { fieldNormalizer } from '~/services/api/contracts/fieldNormalizer'

interface StoreUpdateDetailsContext {
  readonly store_status: StoreStatus
  /**
   * A real provider lookup ran against the typed-but-unpicked address and found no match, and
   * the user has not yet confirmed they want to save it anyway. Only this state blocks submit;
   * a lookup that never ran (provider down) or one the user simply didn't select never does.
   */
  readonly addressNeedsConfirmation?: boolean
}

const IMAGE = { mimes: ['jpeg', 'jpg', 'png', 'webp'], maxKb: 5120 } as const
// Typed text alone is a valid address value -- geocoding a suggestion is optional, never required.
const ADDRESS_REQUIRED = { field: 'address', message: 'address_required' } as const
const REQUIRED_TEXT = ['name', 'phone'] as const
const NULLABLE_TEXT = [
  'address',
  'city',
  'description',
  'email',
  'google_places_address',
  'state',
  'zip'
] as const

// UpdateStoreDetailsRequest::prepareForValidation
const normalize = fieldNormalizer({
  trimmed: REQUIRED_TEXT,
  blankToNull: NULLABLE_TEXT,
  website: ['website']
})

const schema = StoreUpdateDetailsRequestSchema.extend({
  cover: fileUpload(IMAGE).nullish(),
  email: email(255).nullish(),
  google_places_address: z.string().max(GOOGLE_PLACES_ADDRESS_MAX).nullish(),
  logo: fileUpload(IMAGE).nullish(),
  phone: phone.optional()
}).strict()

export const StoreUpdateDetails = (
  context: StoreUpdateDetailsContext
): z.ZodType<z.output<typeof schema>> =>
  z.preprocess(normalize, schema).superRefine((value, issues) => {
    if (!isStoreOnboardingEditable(context.store_status)) return

    if (value[ADDRESS_REQUIRED.field] === null) {
      issues.addIssue({ code: 'custom', message: ADDRESS_REQUIRED.message, path: ['address'] })
    }
    if (context.addressNeedsConfirmation === true) {
      issues.addIssue({ code: 'custom', message: 'address_unresolved', path: ['address'] })
    }
  })
